import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import FunASRClient from "../../utils/FunASRClient";

/**
 * 群聊输入区
 * 支持 @ 提及弹出成员/Agent 列表；支持通过 ref.insertMention(name) 插入 @提及
 */
const MessageInput = forwardRef(({ onSend, members, agents, disabled, statusHint = "" }, ref) => {
  const { t } = useTranslation();
  const [content, setContent] = useState("");
  const [showMention, setShowMention] = useState(false);
  const [mentionFilter, setMentionFilter] = useState("");
  const [mentionAnchor, setMentionAnchor] = useState(null);
  const [isVoiceRecording, setIsVoiceRecording] = useState(false);
  const [isVoiceProcessing, setIsVoiceProcessing] = useState(false);
  const [voiceError, setVoiceError] = useState("");
  const inputRef = useRef(null);
  const voiceRecognitionRef = useRef(null);

  const mentionCandidates = [
    ...members.map((m) => ({ id: m.userId, name: m.username, type: "member" })),
    ...agents.map((a) => ({ id: a.agentId, name: a.displayName, type: "agent" })),
  ].filter((c) => !mentionFilter || c.name.toLowerCase().includes(mentionFilter.toLowerCase()));

  const handleInputChange = (e) => {
    const text = e.target.value;
    setContent(text);

    const lastAt = text.lastIndexOf("@");
    if (lastAt >= 0) {
      const after = text.slice(lastAt + 1);
      if (!/\s/.test(after)) {
        setShowMention(true);
        setMentionFilter(after);
        setMentionAnchor(lastAt);
        return;
      }
    }
    setShowMention(false);
  };

  const handleSelectMention = (item) => {
    if (mentionAnchor === null) return;
    const before = content.slice(0, mentionAnchor);
    const rest = content.slice(mentionAnchor);
    const insert = `@${item.name} `;
    const newRest = rest.replace(/@[^\s]*/, insert);
    setContent(before + newRest);
    setShowMention(false);
    setMentionAnchor(null);
    inputRef.current?.focus();
  };

  useImperativeHandle(ref, () => ({
    insertMention: (name) => {
      setContent((prev) => (prev ? prev + `@${name} ` : `@${name} `));
      setShowMention(false);
      inputRef.current?.focus();
    },
  }), []);

  useEffect(() => {
    const recognition = new FunASRClient({ stopTimeoutMs: 6000 });
    recognition.onstart = () => {
      setIsVoiceRecording(true);
      setIsVoiceProcessing(false);
      setVoiceError("");
    };
    recognition.onend = () => {
      setIsVoiceRecording(false);
      setIsVoiceProcessing(false);
    };
    recognition.onresult = (event) => {
      const transcript = event.results?.[0]?.[0]?.transcript || "";
      if (!transcript.trim()) return;
      setContent((prev) => (prev ? `${prev} ${transcript}` : transcript));
      inputRef.current?.focus();
    };
    recognition.onerror = (event) => {
      console.error("[GroupChat MessageInput] 语音识别错误:", event.error);
      setIsVoiceRecording(false);
      setIsVoiceProcessing(false);
      setVoiceError("语音识别暂不可用，请检查麦克风权限或稍后重试。");
    };
    voiceRecognitionRef.current = recognition;
    return () => {
      try {
        voiceRecognitionRef.current?.dispose?.();
      } catch (_) {}
      voiceRecognitionRef.current = null;
    };
  }, []);

  const handleSend = () => {
    const trimmed = content.trim();
    if (!trimmed || disabled) return;

    const mentions = [];
    const mentionRegex = /@([^\s@]+)/g;
    let m;
    while ((m = mentionRegex.exec(trimmed))) {
      const key = m[1];
      const agent = agents.find(
        (a) => a.displayName === key || (a.agentId && a.agentId === key)
      );
      if (agent) mentions.push(agent.agentId);
    }

    onSend({ content: trimmed, mentions });
    setContent("");
  };

  const handleVoiceClick = () => {
    if (!voiceRecognitionRef.current || disabled) return;
    setVoiceError("");
    if (isVoiceRecording) {
      voiceRecognitionRef.current.stop();
      setIsVoiceProcessing(true);
      return;
    }
    try {
      voiceRecognitionRef.current.start();
    } catch (error) {
      console.error("[GroupChat MessageInput] 启动语音失败:", error);
      setVoiceError("无法启动麦克风，请检查浏览器权限。");
    }
  };

  const handleCancelVoice = () => {
    try {
      voiceRecognitionRef.current?.abort?.();
    } catch (_) {}
    setIsVoiceRecording(false);
    setIsVoiceProcessing(false);
    setVoiceError("");
  };

  return (
    <div className="groupchat-input-shell">
      {statusHint ? (
        <div className="groupchat-ai-queue-hint" aria-live="polite">
          {statusHint}
        </div>
      ) : null}
      <div className="groupchat-input-area">
        <div className="input-wrapper">
          <textarea
            ref={inputRef}
            value={content}
            onChange={handleInputChange}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder={t("groupchat.input_placeholder")}
            rows={2}
            disabled={disabled}
          />
          {showMention && mentionCandidates.length > 0 && (
            <div className="mention-dropdown">
              {mentionCandidates.slice(0, 8).map((c) => (
                <div
                  key={c.id}
                  className="mention-item"
                  onClick={() => handleSelectMention(c)}
                >
                  {c.type === "agent" ? "🤖 " : "👤 "}
                  {c.name}
                </div>
              ))}
            </div>
          )}
          {(isVoiceRecording || isVoiceProcessing || voiceError) && (
            <div className={`voice-status-panel ${voiceError ? "error" : ""}`}>
              <span>
                {voiceError || (isVoiceProcessing ? "正在识别语音，结果会填入输入框..." : "正在录音，再次点击麦克风结束")}
              </span>
              {(isVoiceRecording || isVoiceProcessing || voiceError) && (
                <button type="button" onClick={handleCancelVoice}>
                  取消
                </button>
              )}
            </div>
          )}
        </div>
        <button
          type="button"
          className={`groupchat-voice-btn ${isVoiceRecording ? "recording" : ""}`}
          onClick={handleVoiceClick}
          disabled={disabled || isVoiceProcessing}
          title={isVoiceRecording ? "结束录音并转写" : "语音输入"}
        >
          {isVoiceProcessing ? "识别中" : isVoiceRecording ? "结束" : "语音"}
        </button>
        <button onClick={handleSend} disabled={!content.trim() || disabled}>
          {disabled ? t("groupchat.waiting") : t("groupchat.send")}
        </button>
      </div>
    </div>
  );
});

MessageInput.displayName = "MessageInput";

export default MessageInput;
